import api from '@/lib/axios';
import { downloadExport, ExportFormat } from '@/services/school.api';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export interface MarkEntryRow {
  schoolId: string;
  schoolName: string;
  candidateItemId: string;
  candidateId: string;
  studentName: string;
  chestNumber: number | null;
  groupLeaderId: string | null;
  isGroupLeader: boolean;
  judgeMark: number | null;
  grade: string | null;
  rank: number | null;
}

export interface SetMarkData {
  schoolId: string;
  candidateItemId: string;
  judgeMark?: number;
  grade?: 'A' | 'B' | 'C';
}

export interface ItemResultRow {
  rank: number | null;
  candidateItemId: string;
  studentName: string;
  schoolId: string;
  schoolName: string;
  chestNumber: number | null;
  judgeMark: number | null;
  grade: string | null;
  isGroup: boolean;
  isGroupLeader: boolean;
}

export interface ScoreboardRow {
  schoolId: string;
  schoolName: string;
  points: number;
  items: Record<string, string>;
}

export interface Scoreboard {
  items: Record<string, string>;
  schools: ScoreboardRow[];
}

export interface CertificateStatus {
  status: 'ready' | 'generating';
  pdfUrl: string | null;
}

export type ExportType = 'results' | 'call-sheet' | 'participant-list';

export interface ProgramItem {
  id: string;
  legacyId: string | null;
  name: string;
  code: string | null;
  type: 'single' | 'group' | 'team';
  gradeCategory: string | null;
  maxMarks: number | null;
  quranProgram: boolean;
  indications: string | null;
  remarks: string | null;
}

export type CreateProgramItemInput = Omit<ProgramItem, 'id' | 'legacyId'>;

export const ProgramItemsApi = {
  list: async (): Promise<ProgramItem[]> => unwrap(await api.get('/superadmin/program-items')),
  get: async (id: string): Promise<ProgramItem> => unwrap(await api.get(`/superadmin/program-items/${id}`)),
  create: async (data: CreateProgramItemInput): Promise<ProgramItem> => unwrap(await api.post('/superadmin/program-items', data)),
  update: async (id: string, data: Partial<CreateProgramItemInput>): Promise<ProgramItem> =>
    unwrap(await api.patch(`/superadmin/program-items/${id}`, data)),
  remove: async (id: string) => unwrap(await api.delete(`/superadmin/program-items/${id}`)),
};

// ── Part 4: platform-tier cross-school candidate management ──────────────

export interface SchoolRegistrationStats {
  schoolId: string;
  schoolName: string;
  male: number;
  female: number;
  total: number;
}

export interface GradeRegistrationStats {
  grade: string;
  male: number;
  female: number;
  total: number;
}

export interface RegistrationStats {
  bySchool: SchoolRegistrationStats[];
  byGrade: GradeRegistrationStats[];
}

export interface AllStudentsRow {
  candidateId: string;
  studentId: string;
  studentName: string;
  schoolId: string;
  schoolName: string;
  studentGrade: string | null;
  gender: string | null;
  mobile: string | null;
  chestNumber: number | null;
  totalPrograms: number;
}

export interface CandidateItemDetailRow {
  snNo: number;
  programItemName: string;
  gradeCategory: string | null;
  judgeMark: number | null;
  grade: string | null;
  rank: number | null;
}

export interface CandidateItemDetails {
  studentName: string;
  schoolName: string;
  chestNumber: number | null;
  items: CandidateItemDetailRow[];
}

export interface AcademicYearOption {
  name: string;
  isCurrent: boolean;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AdminCandidateItem {
  id: string;
  programItemName: string;
  programItemType: 'single' | 'group' | 'team';
  isGroupLeader: boolean;
  judgeMark: number | null;
  grade: string | null;
  rank: number | null;
}

export interface AdminProgramCandidate {
  id: string;
  schoolId: string;
  schoolName: string;
  studentId: string;
  studentName: string;
  admissionNo: string;
  className: string;
  sectionName: string;
  chestNumber: number | null;
  oldChestNumber: string | null;
  items: AdminCandidateItem[];
  createdAt: string;
  updatedAt: string;
}

export const ProgramCandidatesAdminApi = {
  list: async (params: { schoolId?: string; grade?: string; search?: string; page?: number; limit?: number }): Promise<PaginatedResult<AdminProgramCandidate>> =>
    unwrap(await api.get('/superadmin/program-candidates', { params })),

  update: async (id: string, data: { schoolId: string; chestNumber?: number; oldChestNumber?: string }) =>
    unwrap(await api.patch(`/superadmin/program-candidates/${id}`, data)),

  remove: async (id: string, schoolId: string) =>
    unwrap(await api.delete(`/superadmin/program-candidates/${id}`, { params: { schoolId } })),

  exportCandidates: (grade: string) => downloadExport(`/superadmin/program-candidates/export/${encodeURIComponent(grade)}`, `program_candidates_${grade}`, 'xlsx'),

  exportCandidateItems: (grade: string) => downloadExport(`/superadmin/program-candidate-items/export/${encodeURIComponent(grade)}`, `program_candidate_items_${grade}`, 'xlsx'),

  exportResult: (grade: string, academicYear?: string) =>
    downloadExport(`/superadmin/program-dashboard/export-result/${encodeURIComponent(grade)}`, `program_result_${grade}`, 'xlsx', { academicYear }),

  // Grouped PDF — one section per program item, each sorted by mark descending (mirrors legacy printResult()).
  // Distinct from ProgramDashboardApi.exportData('results', 'pdf', ...), which is one flat, ungrouped list.
  printResult: (grade: string, academicYear?: string) =>
    downloadExport(`/superadmin/program-dashboard/print-result/${encodeURIComponent(grade)}`, `program_candidates_result_${grade}`, 'pdf', { academicYear }),

  exportParticipantsExcel: (schoolId: string, schoolName: string, academicYear?: string) =>
    downloadExport(`/superadmin/program-dashboard/export-participants/${schoolId}`, `program_participants_${schoolName}`, 'xlsx', { academicYear }),

  exportParticipantsPdf: (schoolId: string, schoolName: string) => downloadExport('/superadmin/program-candidates/pdf', `program_participants_${schoolName}`, 'pdf', { schoolId }),
};

export const ProgramDashboardAdminApi = {
  registrationStats: async (academicYear?: string): Promise<RegistrationStats> =>
    unwrap(await api.get('/superadmin/program-dashboard/registration-stats', { params: { academicYear } })),

  /** `grade` = the student's own school class (legacy: classes.name). `gradeCategory` = the competition's grade bracket (program_items.grade_category, e.g. "Grade 3") — a different concept, do not conflate. */
  allStudents: async (params: {
    schoolId?: string;
    gender?: string;
    grade?: string;
    gradeCategory?: string;
    programItemId?: string;
    search?: string;
    academicYear?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResult<AllStudentsRow>> => unwrap(await api.get('/superadmin/program-dashboard/all-students', { params })),

  candidateItemDetails: async (schoolId: string, candidateId: string): Promise<CandidateItemDetails> =>
    unwrap(await api.get('/superadmin/program-dashboard/all-students/candidate-details', { params: { schoolId, candidateId } })),

  academicYears: async (): Promise<AcademicYearOption[]> => unwrap(await api.get('/superadmin/program-dashboard/academic-years')),
};

export const ProgramDashboardApi = {
  markEntry: async (itemId: string, academicYear?: string): Promise<MarkEntryRow[]> =>
    unwrap(await api.get('/superadmin/program-dashboard/mark-entry', { params: { itemId, academicYear } })),

  setMark: async (data: SetMarkData): Promise<{ updated: number }> =>
    unwrap(await api.patch('/superadmin/program-dashboard/mark', data)),

  recalculateRanks: async (itemId: string): Promise<{ itemsRanked: number }> =>
    unwrap(await api.post(`/superadmin/program-dashboard/program-items/${itemId}/recalculate-ranks`)),

  scoreboard: async (grade?: string, academicYear?: string): Promise<Scoreboard> =>
    unwrap(await api.get('/superadmin/program-dashboard/scoreboard', { params: { grade, academicYear } })),

  itemResults: async (itemId: string, academicYear?: string): Promise<ItemResultRow[]> =>
    unwrap(await api.get('/superadmin/program-dashboard/item-results', { params: { item_id: itemId, academicYear } })),

  itemWiseTopScore: async (itemId: string, academicYear?: string): Promise<ItemResultRow[]> =>
    unwrap(await api.get('/superadmin/program-dashboard/item-wise-top-score', { params: { item_id: itemId, academicYear } })),

  exportData: (type: ExportType, format: ExportFormat, params?: { grade?: string; schoolId?: string; academicYear?: string }) =>
    downloadExport('/superadmin/program-dashboard/export', `program-${type}`, format, { type, ...params }),

  exportItemResults: (itemId: string, academicYear?: string) =>
    downloadExport('/superadmin/program-dashboard/export-item-results', 'program-item-results', 'xlsx', { item_id: itemId, academicYear }),

  certificate: async (schoolId: string, candidateItemId: string): Promise<CertificateStatus> =>
    unwrap(await api.get('/superadmin/program-dashboard/certificate', { params: { schoolId, candidateItemId } })),
};
